import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { Info } from "lucide-react";
import GenericModal from "../../GenericModal";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import Flag from "../../Flag";
import {
  searchAvailablePhoneNumbers,
  purchasePhoneNumber,
  replacePhoneNumber,
  getAllPhoneNumbers,
} from "../../../apis/phoneInventory";

export default function ReplaceNumberModal({ isOpen, currentNumber, onClose, workspaceId, onSuccess }) {
  const queryClient = useQueryClient();
  const [replacementOption, setReplacementOption] = useState("buy_new");
  const [selectedExistingNumber, setSelectedExistingNumber] = useState(null);
  const [showNumberSelector, setShowNumberSelector] = useState(true);

  // Reset state whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setReplacementOption("buy_new");
      setSelectedExistingNumber(null);
      setShowNumberSelector(true);
    }
  }, [isOpen]);

  const currentNumberId = currentNumber?.originalId || currentNumber?.id;

  // Build capabilities param matching the spam number (voice/sms/mms, exclude fax)
  const capabilityMap = { voice: "voice", sms: "sms", mms: "mms" };
  const capabilitiesParam = currentNumber?.capabilities
    ? Object.entries(currentNumber.capabilities)
        .filter(([key, val]) => val && capabilityMap[key])
        .map(([key]) => capabilityMap[key])
    : ["voice", "sms"];

  // Fetch a suggested replacement number matching country, number_type and capabilities
  const { data: suggestionData, isLoading: isSuggestionLoading } = useQuery(
    ["replacement_suggestion", currentNumber?.country, currentNumber?.number_type, workspaceId],
    () =>
      searchAvailablePhoneNumbers({
        queryParams: {
          country: currentNumber?.country,
          number_type: currentNumber?.number_type,
          address_requirements: "any",
          limit: 5,
          capabilities: capabilitiesParam,
        },
        options: { headers: { "X-Workspace-Id": workspaceId } },
      }),
    {
      enabled: isOpen && !!currentNumber?.country && replacementOption === "buy_new",
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  const suggestionList = suggestionData?.data || (Array.isArray(suggestionData) ? suggestionData : []);
  const suggestedNumber = suggestionList[0] || null;

  // Fetch existing phone numbers (for "replace existing" flow)
  const { data: phoneNumbersData, isLoading: isPhoneNumbersLoading } = useQuery(
    ["phone_numbers_for_replacement", workspaceId],
    () =>
      getAllPhoneNumbers({
        query: "",
        options: { headers: { "X-Workspace-Id": workspaceId } },
      }),
    {
      enabled: isOpen && replacementOption === "replace_existing",
      refetchOnWindowFocus: false,
    }
  );

  const rawNumbers = phoneNumbersData?.data || (Array.isArray(phoneNumbersData) ? phoneNumbersData : []);
  const availableNumbers = rawNumbers.filter((n) => n.id !== currentNumberId && n.spam_status !== "flagged");

  const replaceMutation = useMutation(replacePhoneNumber, {
    onSuccess: () => {
      toast.success("Phone number replaced successfully!");
      queryClient.invalidateQueries("get_phone_numbers_admin");
      queryClient.invalidateQueries("get_phone_numbers_user");
      if (onSuccess) onSuccess();
      onClose();
    },
    onError: (error) => {
      console.error("Error replacing phone number:", error);
      toast.error("Failed to replace phone number. Please try again.");
    },
  });

  const confirmMutation = useMutation(purchasePhoneNumber, {
    onSuccess: (response) => {
      const newPhoneNumberId = response?.data?.id || response?.id;
      replaceMutation.mutate({
        data: {
          old_phone_number_id: currentNumberId,
          new_phone_number_id: newPhoneNumberId,
        },
        options: { headers: { "X-Workspace-Id": workspaceId } },
      });
    },
    onError: (error) => {
      console.error("Error purchasing phone number:", error);
      toast.error("Failed to replace phone number. Please try again.");
    },
  });

  const handleConfirm = () => {
    if (!currentNumber) return;

    if (replacementOption === "buy_new") {
      const isAssigned = (currentNumber?.type || "Assigned") === "Assigned";
      confirmMutation.mutate({
        data: {
          phone_number: suggestedNumber?.phone_number,
          label: suggestedNumber?.phone_number,
          type: currentNumber?.type || "Assigned",
          assign_to_users: isAssigned ? (currentNumber?.assigned_users || []).map((u) => u.id) : undefined,
        },
        options: { headers: { "X-Workspace-Id": workspaceId } },
      });
    } else {
      if (!selectedExistingNumber) {
        toast.error("Please select a number to replace with.");
        return;
      }
      replaceMutation.mutate({
        data: {
          old_phone_number_id: currentNumberId,
          new_phone_number_id: selectedExistingNumber?.id,
        },
        options: { headers: { "X-Workspace-Id": workspaceId } },
      });
    }
  };

  const handleOptionChange = (option) => {
    setReplacementOption(option);
    setSelectedExistingNumber(null);
    setShowNumberSelector(true);
  };

  const handleExistingNumberSelect = (selectedId) => {
    if (!selectedId) {
      setSelectedExistingNumber(null);
      return;
    }
    const found = availableNumbers.find((n) => n.id === selectedId);
    setSelectedExistingNumber(found || null);
    if (found) setShowNumberSelector(false);
  };

  const isConfirming = confirmMutation.isLoading || replaceMutation.isLoading;
  const canConfirm = replacementOption === "buy_new" || !!selectedExistingNumber;

  return (
    <GenericModal isOpen={isOpen} closeModal={onClose} dismiss={true} width="w-[560px]">
      {/* Header */}
      <div className="flex items-start gap-4 mb-5 pr-6">
        <div className="w-12 h-12 bg-background border border-[#E9EAEB] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 21L9 18M9 18L12 15M9 18H14C17.866 18 21 14.866 21 11C21 8.2076 19.3649 5.7971 17 4.67363M5 17.3264C2.63505 16.2029 1 13.7924 1 11C1 7.13401 4.13401 4 8 4H13M13 4L10 1M13 4L10 7"
              stroke="#414651"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[#181D27]">Replace {currentNumber?.phone_number}</h2>
          <p className="text-[13.5px] text-[#535862] mt-1">
            This number has been flagged as spam. Choose how to proceed.
          </p>
        </div>
      </div>

      <hr className="mb-4 -mx-6 border-[#E9EAEB]" />

      {/* Option 1: Buy new number */}
      <div onClick={() => handleOptionChange("buy_new")} className="cursor-pointer">
        <div className="flex items-start gap-3">
          <input
            type="radio"
            id="replace_buy_new"
            name="replacement_option"
            checked={replacementOption === "buy_new"}
            onChange={() => handleOptionChange("buy_new")}
            onClick={(e) => e.stopPropagation()}
            className="mt-0.5 h-4 w-4 text-[#9E77ED] focus:ring-[#9E77ED] cursor-pointer"
          />
          <div className="flex-1">
            <label htmlFor="replace_buy_new" className="text-sm font-semibold text-[#414651]cursor-pointer">
              Buy new number
            </label>
            <p className="text-sm text-[#535862]">Replace the spam number with a new fresh number</p>
          </div>
        </div>

        {replacementOption === "buy_new" && (
          <div className="mt-3 ml-7">
            {isSuggestionLoading ? (
              <div className="rounded-lg p-3 bg-[#FAFAFA] flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#D9D6FE] border-t-[#7F56D9]" />
                <span className="text-sm text-gray-500">Finding available number...</span>
              </div>
            ) : suggestedNumber?.phone_number ? (
              <div className="rounded-lg p-3 bg-[#FAFAFA]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[28px] rounded-full inline-flex items-center justify-center overflow-hidden border border-border leading-none">
                    <Flag code={currentNumber?.country} squared />
                  </span>
                  <span className="text-sm font-semibold text-[#D92D20] line-through">
                    {currentNumber?.phone_number}
                  </span>
                  <span className="text-[#000000]">→</span>
                  <span className="text-sm font-semibold text-[#181D27]">{suggestedNumber.phone_number}</span>
                  <Badge variant="brand" className="text-xs">
                    New
                  </Badge>
                </div>
                <div className="flex items-start gap-1.5 mt-[18px]">
                  <Info className="h-3.5 w-3.5 text-[#414651] mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-[#535862]">
                    After 30 days, your old number will be retired. Calls will be forwarded for 30 days.
                  </p>
                </div>
              </div>
            ) : (
              <div className="border border-border rounded-lg p-3 bg-gray-50">
                <p className="text-sm text-gray-500">
                  A new number will be assigned automatically upon confirmation.
                </p>
                <div className="flex items-start gap-1.5 mt-2">
                  <Info className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-500">
                    After 30 days, your old number will be retired. Calls will be forwarded for 30 days.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Option 2: Replace with existing number */}
      <div className="mt-4 cursor-pointer" onClick={() => handleOptionChange("replace_existing")}>
        <div className="flex items-start gap-3">
          <input
            type="radio"
            id="replace_existing"
            name="replacement_option"
            checked={replacementOption === "replace_existing"}
            onChange={() => handleOptionChange("replace_existing")}
            onClick={(e) => e.stopPropagation()}
            className="mt-0.5 h-4 w-4 text-[#9E77ED] focus:ring-[#9E77ED] cursor-pointer"
          />
          <div className="flex-1">
            <label htmlFor="replace_existing" className="text-sm font-semibold text-[#414651] cursor-pointer">
              Replace with existing number
            </label>
            <p className="text-sm text-[#535862]">Assign an already existing number to replace this number</p>
          </div>
        </div>

        {replacementOption === "replace_existing" && (
          <div className="mt-3 ml-7" onClick={(e) => e.stopPropagation()}>
            {showNumberSelector || !selectedExistingNumber ? (
              isPhoneNumbersLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#D9D6FE] border-t-[#7F56D9]" />
                  <span className="text-sm text-gray-500">Loading numbers...</span>
                </div>
              ) : (
                <Select value={selectedExistingNumber?.id || ""} onValueChange={handleExistingNumberSelect}>
                  <SelectTrigger className="w-full h-10">
                    <SelectValue placeholder="Select a number" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableNumbers.map((n) => (
                      <SelectItem key={n.id} value={n.id}>
                        {n.label && n.label !== n.phone_number
                          ? `${n.label} (${n.phone_number})`
                          : n.phone_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )
            ) : (
              <div className="border border-border rounded-lg p-3 bg-gray-50">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[28px] rounded-full inline-flex items-center justify-center overflow-hidden border border-border leading-none">
                    <Flag code={currentNumber?.country} squared />
                  </span>
                  <span className="text-sm font-semibold text-[#D92D20] line-through">
                    {currentNumber?.phone_number}
                  </span>
                  <span className="text-[#000000]">→</span>
                  <span className="text-sm font-semibold text-[#181D27]">
                    {selectedExistingNumber?.phone_number}
                  </span>
                  <button
                    type="button"
                    className="text-sm text-[#7F56D9] hover:text-purple-700 font-medium ml-1"
                    onClick={() => setShowNumberSelector(true)}
                  >
                    Change
                  </button>
                </div>
                <div className="flex items-start gap-1.5 mt-2">
                  <Info className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-500">
                    After 30 days, your old number will be retired. Calls will be forwarded for 30 days.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <hr className="mt-8 -mx-6 border-[#E9EAEB]" />
      {/* Footer */}
      <div className="flex gap-3 mt-6">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={isConfirming}
          className="flex-1 border border-[#D5D7DA] text-gray-700"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isConfirming || !canConfirm}
          className="flex-1 bg-[#7F56D9] hover:bg-[#6D47C7] text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isConfirming ? (
            <span className="flex items-center gap-2">
              Confirming...
              <div className="loader_circle" />
            </span>
          ) : (
            "Confirm"
          )}
        </Button>
      </div>
    </GenericModal>
  );
}
